import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import useSEO, { orgSchema, breadcrumbSchema } from "@/lib/useSEO";
import { http } from "@/lib/api";
import { Loader2, BookOpen, ArrowRight } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "The PixelEmpire Blog — Permanent Advertising, SEO & Startup Growth",
    description:
      "Insights on permanent digital advertising, SEO backlinks, startup marketing, and the future of independent web culture. Long-form articles from the PixelEmpire team.",
    path: "/blog",
    jsonLd: [
      orgSchema,
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "PixelEmpire Blog",
        url: "https://pixel-empires.com/blog",
      },
    ],
  });

  useEffect(() => {
    let alive = true;
    http
      .get("/blog/posts")
      .then(({ data }) => {
        if (alive) {
          setPosts(data.posts || []);
          setLoading(false);
        }
      })
      .catch(() => setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <PageShell testId="page-blog">
      <div className="pill" data-testid="blog-pill">
        <BookOpen className="w-3.5 h-3.5" /> <span>Blog</span>
      </div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-white" data-testid="blog-h1">
        Learn to build a <span className="text-gradient">permanent</span> internet presence
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-3xl">
        Long-form essays on permanent digital advertising, SEO backlinks, startup growth, and why
        indie founders should think in decades — not quarters.
      </p>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-[var(--fg-muted)]" data-testid="blog-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading articles…
        </div>
      ) : (
        <ul className="mt-10 space-y-5" data-testid="blog-list">
          {posts.map((p, i) => (
            <li
              key={p.slug}
              className="glass p-6 rounded-2xl hover:border-cyan-400/30 transition"
              data-testid={`blog-item-${p.slug}`}
            >
              <Link to={`/blog/${p.slug}`} className="block group">
                <div className="text-xs font-mono text-[var(--fg-muted)]">
                  #{i + 1} · {new Date(p.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  {p.tags?.length ? ` · ${p.tags.slice(0, 2).join(" · ")}` : ""}
                </div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl leading-tight text-white group-hover:text-cyan-300 transition">
                  {p.title}
                </h2>
                <p className="mt-2 text-sm sm:text-base text-[var(--fg-muted)] leading-relaxed">
                  {p.excerpt}
                </p>
                <div className="mt-4 text-sm text-cyan-300 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read article <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
}
